/**
 * VehicleState — pure vehicle HP + mount/dismount logic.
 * No Phaser imports — fully testable in Node.js.
 *
 * Vehicles (forklift, downed biker's bike) can be mounted by the player.
 * While mounted, incoming hits redirect to vehicle HP instead of the player.
 * When HP reaches 0 the vehicle is destroyed and the player is forcibly
 * dismounted with a brief stun (DISMOUNT_STUN_FRAMES, applied in Player).
 */

export type VehicleKind = 'forklift' | 'bike' | 'tube-trolley' | 'market-scooter' | 'moped';

export interface VehicleDef {
  kind: VehicleKind;
  maxHp: number;
  /** Damage dealt to enemies per RAM contact hit. */
  ramDamage: number;
  /** Knockback (px) applied to rammed enemies. */
  ramKnockback: number;
  /** Hitstun (frames) applied to rammed enemies. */
  ramHitstun: number;
  /** Forward reach of the ram hitbox from player centre (px). */
  ramRadius: number;
  /** Player movement speed multiplier while mounted. */
  speedMult: number;
}

export interface VehicleState {
  hp: number;
  readonly maxHp: number;
  isMounted: boolean;
  isDestroyed: boolean;
}

export interface VehicleHitResult {
  nextState: VehicleState;
  /** True on the single frame the vehicle's HP first reaches 0. */
  justDestroyed: boolean;
}

/** Create fresh (full HP, un-mounted) vehicle state from its definition. */
export function createVehicleState(def: VehicleDef): VehicleState {
  return {
    hp: def.maxHp,
    maxHp: def.maxHp,
    isMounted: false,
    isDestroyed: false,
  };
}

/** Set the vehicle as mounted. No-op if already mounted or destroyed. */
export function mountVehicle(state: VehicleState): VehicleState {
  if (state.isDestroyed || state.isMounted) return state;
  return { ...state, isMounted: true };
}

/** Voluntarily dismount — player chose to leave the vehicle. */
export function dismountVehicle(state: VehicleState): VehicleState {
  if (!state.isMounted) return state;
  return { ...state, isMounted: false };
}

/**
 * Apply an enemy hit to the vehicle.
 * Returns the updated state and a flag set only on the destruction frame.
 * If the vehicle is already destroyed, state is unchanged and justDestroyed
 * is false.
 */
export function vehicleReceiveHit(state: VehicleState, damage: number): VehicleHitResult {
  if (state.isDestroyed) {
    return { nextState: state, justDestroyed: false };
  }
  const hp = Math.max(0, state.hp - damage);
  const justDestroyed = hp <= 0;
  return {
    nextState: {
      ...state,
      hp,
      isDestroyed: justDestroyed,
      // Auto-dismount the player when the vehicle is destroyed.
      isMounted: justDestroyed ? false : state.isMounted,
    },
    justDestroyed,
  };
}
